import React, { useEffect, useState } from 'react';
import SwipeableViews from 'react-swipeable-views';
import { Avatar, Box, MobileStepper, styled, Typography } from '@mui/material';
import SideCoverIcon from '@stack/assets/images/side-cover-icon.png';
import SideCoverMain from '@stack/assets/images/side-cover-main.jpeg';
import SideCoverRight from '@stack/assets/images/side-cover-main.png';
import SideCoverProgress from '@stack/assets/images/side-cover-progress.png';
import SideCoverSuccess from '@stack/assets/images/side-cover-success.png';
import SliderQuote from '@stack/assets/images/slider-quote.png';
import Image, { StaticImageData } from 'next/image';

import { NoticeType, PageType, SideCoverProps } from './props';

// Override Text color in this component
const CustomTypography = styled(Typography)(() => ({
  color: '#ffffff',
}));

// Override Stepper dot color in this component
const CustomStepper = styled(MobileStepper)(() => ({
  background: 'transparent',
  padding: 0,
  marginTop: '24px',
  '.MuiMobileStepper-dot': {
    backgroundColor: 'rgba(255,255,255,0.26)',
  },
  '.MuiMobileStepper-dotActive': {
    backgroundColor: '#ffffff',
  },
}));

export const SideCover: React.FC<SideCoverProps> = (props) => {
  const {
    pageType,
    noticeList = [],
    backgroundImage,
    footerText,
    copyRight = `Copyright © ${new Date().getFullYear()} Wesettle Sdn Bhd. All Rights Reserved.`,
    isCoverLeft = false,
  } = props;

  const backgroundImageMap = {
    [PageType.MAIN]: isCoverLeft
      ? (SideCoverMain as unknown as StaticImageData)
      : (SideCoverRight as unknown as StaticImageData),
    [PageType.SIGN_UP]: SideCoverProgress as unknown as StaticImageData,
    [PageType.SUCCESS]: SideCoverSuccess as unknown as StaticImageData,
  };
  const totalSlides = noticeList?.length;

  // ======================= STATE
  const [index, setIndex] = useState(0);

  // ======================= EVENTS
  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prevIndex) => (prevIndex + 1) % totalSlides);
    }, 3000);

    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ======================= RENDER FUNCTIONS
  const renderProcessItem = (item: NoticeType) => {
    return (
      <Box>
        <Box>
          <Image src={SliderQuote} alt={'icon'} height={50} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', my: '25px' }}>
          <Avatar
            sx={{ border: 2, mr: 3 }}
            src={item.avatarUrl}
            alt={item.name}
          />
          <Box>
            <CustomTypography variant={'h6'} className={'font-semibold'}>
              {item.name}
            </CustomTypography>
            <CustomTypography variant={'h6'} className={'font-semibold'}>
              {item.position}
            </CustomTypography>
          </Box>
        </Box>
        <CustomTypography>{item.description}</CustomTypography>
      </Box>
    );
  };

  const renderSuccessItem = (item: NoticeType) => {
    return (
      <Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '28px',
          }}
        >
          <Avatar
            sx={{ border: 2, height: 100, width: 100 }}
            src={item.avatarUrl}
            alt={item.name}
          />
          <Box>
            <CustomTypography
              variant={'h6'}
              className={'font-semibold text-center'}
            >
              {item.name}
            </CustomTypography>
            <CustomTypography
              variant={'h6'}
              className={'font-semibold text-center'}
            >
              {item.position}
            </CustomTypography>
          </Box>
          <CustomTypography>{item.description}</CustomTypography>
        </Box>
      </Box>
    );
  };

  const renderContent = (noticeList: NoticeType[]) => {
    if (pageType === PageType.SIGN_UP) {
      return (
        <Box>
          <SwipeableViews index={index} onChangeIndex={setIndex}>
            {noticeList.map((item, i) => {
              return Math.abs(index - i) <= 2 && renderProcessItem(item);
            })}
          </SwipeableViews>
          <CustomStepper
            steps={totalSlides}
            position="static"
            activeStep={index}
            backButton={null}
            nextButton={null}
          />
        </Box>
      );
    }
    if (pageType === PageType.SUCCESS) {
      return (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: '28px',
          }}
        >
          <SwipeableViews index={index} onChangeIndex={setIndex}>
            {noticeList.map((item, i) => {
              return Math.abs(index - i) <= 2 && renderSuccessItem(item);
            })}
          </SwipeableViews>
          <CustomStepper
            steps={totalSlides}
            position="static"
            activeStep={index}
            backButton={null}
            nextButton={null}
          />
          <CustomTypography>{footerText}</CustomTypography>
          <Image height={150} src={SideCoverIcon} alt={'side-cover-icon'} />
        </Box>
      );
    }
    return null;
  };

  // ======================= VIEWS
  return (
    <Box
      sx={{
        display: {
          xs: pageType === PageType.MAIN ? 'block' : 'none',
          lg: 'block',
        },
        width: {
          xs: '100%',
          lg: pageType === PageType.MAIN && isCoverLeft ? '70%' : '40%',
        },
        height: { xs: '200px', md: '300px', lg: '100vh' },
        position: { xs: 'relative', lg: 'sticky' },
        top: 0,
        order: { xs: 0, lg: pageType === PageType.MAIN && isCoverLeft ? 0 : 1 },
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          backgroundImage: {
            xs: `url(${SideCoverMain.src})`,
            lg: `url(${backgroundImage ?? backgroundImageMap[pageType].src})`,
          },
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          position: 'relative',
          pl: 20,
          pr: 20,
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            minHeight: '80%',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ mt: '20%' }}>
            {noticeList && renderContent(noticeList)}
          </Box>
          {copyRight && pageType === PageType.SIGN_UP && (
            <CustomTypography fontSize={'0.875rem'}>
              {copyRight}
            </CustomTypography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// ======================= EXPORT
export * from './props';
export default SideCover;
